import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase';

export default function PayPurchaseDebtModal({ isOpen, onClose, purchase, onPurchaseUpdated }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amountPaid: '',
    notes: ''
  });
  if (!purchase) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const purchaseRef = doc(db, 'purchases', purchase.id);
      const newAmountPaid = Number(purchase.amountPaid || 0) + Number(formData.amountPaid || 0);
      const newRemainingBalance = purchase.totalAmount - newAmountPaid;
      const updateData = {
        amountPaid: newAmountPaid,
        remainingBalance: newRemainingBalance,
        notes: formData.notes || purchase.notes,
        updatedAt: new Date().toISOString()
      };
      await updateDoc(purchaseRef, updateData);
      // Update supplier's balance in contacts collection
      const supplierRef = doc(db, 'contacts', purchase.supplierId);
      await updateDoc(supplierRef, {
        totalPaid: increment(Number(formData.amountPaid)),
        balance: increment(-Number(formData.amountPaid)),
        updatedAt: new Date().toISOString()
      });
      onPurchaseUpdated({
        ...purchase,
        ...updateData
      });
      onClose();
    } catch (error) {
      console.error('Error updating purchase payment:', error);
      alert('Failed to update payment');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden bg-white dark:bg-gray-800 
               p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Pay Purchase Debt
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 sm:hidden"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Supplier:</span>
                    <span className="font-medium">{purchase.supplierName}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Total Amount:</span>
                    <span className="font-medium">₹{purchase.totalAmount?.toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Amount Paid:</span>
                    <span className="font-medium">₹{(purchase.amountPaid || 0).toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Balance to Pay:</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      ₹{(purchase.remainingBalance || 0).toLocaleString()}
                    </span>
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Payment Amount
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.amountPaid}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setFormData(prev => ({ ...prev, amountPaid: value }));
                        }
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                       text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 text-lg
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                       text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 
                       bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                       hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white 
                       bg-primary-600 rounded-lg border border-transparent
                       hover:bg-primary-700 
                       focus:outline-none focus:ring-2 focus:ring-primary-500
                       disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Processing...' : 'Pay Amount'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
